import * as React from 'react';

import Card, { CardSection } from '@lightspeed/cirrus-card';
import {
  BatchSelect,
  NoDataTd,
  Pager,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@lightspeed/cirrus-table';
import { Bone } from '@lightspeed/flame/Bone';
import { Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Text, TextLink } from '@lightspeed/flame/Text';

import { TdWithLineBreaks } from '../css/migrationErrorTableStyles';
import { LoadingStates, MigrationError } from '../types/MigrationToolTypes';

interface Props {
  loadingStatus: LoadingStates;
  errors: Array<MigrationError>;
  handleRetry: (selectedItems: Array<string>) => void;
}

const PER_PAGE = 5;

const MigrationErrorsTable: React.FC<Props> = ({ loadingStatus, errors, handleRetry }) => {
  const {
    selectedItems,
    itemsSelectedCount,
    toggleAllItems,
    isItemSelected,
    isAllItemsSelected,
    isPartiallySelected,
    toggleItem,
  } = BatchSelect.useBatchSelect(errors, {
    idKey: 'id',
    initialSelectedItems: [],
  });

  const [currentPage, setCurrentPage] = React.useState(1);

  const isLoading = React.useMemo(() => {
    return loadingStatus === 'loading';
  }, [loadingStatus]);

  const totalPages = React.useMemo(() => {
    return Math.ceil(errors.length / PER_PAGE);
  }, [errors]);

  const pageErrors = React.useMemo(() => {
    return errors.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  }, [errors, currentPage]);

  const handleNext = () => {
    setCurrentPage(currentPage + 1);
  };
  const handlePrevious = () => {
    setCurrentPage(currentPage - 1);
  };

  return (
    <Card>
      <CardSection>
        <Table loadingStatus={loadingStatus}>
          <Thead>
            <BatchSelect.HeaderTr
              itemsSelectedCount={itemsSelectedCount}
              selectedItemsText={`${itemsSelectedCount} selected item${
                itemsSelectedCount > 1 ? 's' : ''
              }`}
              checkboxProps={{
                checked: isAllItemsSelected,
                indeterminate: isPartiallySelected,
                onChange: toggleAllItems,
              }}
              innerGroup={
                <BatchSelect.Dropdown title="Actions">
                  {({ toggleOpen }) => (
                    <BatchSelect.DropdownList>
                      <li>
                        <TextLink
                          style={{ display: 'block' }}
                          onClick={() => {
                            toggleOpen();
                            handleRetry(selectedItems);
                          }}
                        >
                          Retry
                        </TextLink>
                      </li>
                    </BatchSelect.DropdownList>
                  )}
                </BatchSelect.Dropdown>
              }
            >
              <Th>Entity</Th>
              <Th>Error</Th>
            </BatchSelect.HeaderTr>
          </Thead>
          <Tbody>
            {!errors.length ? (
              <Tr>
                <NoDataTd width="100%" colSpan={3} textAlign="center">
                  No errors found
                </NoDataTd>
              </Tr>
            ) : (
              pageErrors.map((item) => (
                <BatchSelect.Tr
                  key={item.id}
                  selected={isItemSelected(item.id)}
                  checkboxProps={{
                    checked: isItemSelected(item.id),
                    onChange: () => {
                      toggleItem(item.id);
                    },
                  }}
                >
                  <Td>{item.entityType}</Td>
                  <TdWithLineBreaks>{item.description}</TdWithLineBreaks>
                </BatchSelect.Tr>
              ))
            )}
          </Tbody>
        </Table>
      </CardSection>
      <CardSection>
        <Divider />
        <div className="cr-flex cr-width-full cr-justify-center cr-ph-3 cr-pv-2 no_print">
          <Pager
            isFirst={currentPage === 1}
            isLast={currentPage === totalPages}
            handleNext={isLoading ? undefined : handleNext}
            handlePrevious={isLoading ? undefined : handlePrevious}
          >
            <Flex>
              {isLoading ? (
                <Bone height={3} width="40px" />
              ) : (
                <Text>
                  {totalPages ? currentPage : '0'} of <var>{totalPages}</var>
                </Text>
              )}
            </Flex>
          </Pager>
        </div>
      </CardSection>
    </Card>
  );
};

export default MigrationErrorsTable;
